/* eslint-disable complexity */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { getApiDTNZFormat } from 'helpers';
import { openConfirmation } from 'helpers/Confirmation';
import Select from 'react-select';
import { armTextLength, armDescriptionLength, armReasonLength } from 'appconstants';
import { TextError } from 'components/FormikComponents';
import { EditableField, FormFieldCell, getTodayFormatted } from './FieldHelpers';



export default function EditArmRow({ row, setEditing, editableFields, loadStatusRecords, saveHandler, armData, prtId, selectedCohort }) {
  const formattedDate = getTodayFormatted();

  const statusRecordOptions = [
    { label: "Open", value: "OPEN" },
    { label: "Closed", value: "CLOSED" }
  ];

  const initialValues = {
    pmsId: row?.pmsId || "",
    prtId: prtId || "",
    stratumName: row?.stratumName || "",
    description: row?.description || "",
    status: { label: row?.status || "Open", value: row?.status || "OPEN" },
    statusDate: row?.statusDate || formattedDate,
    statusReason: row?.statusReason || "",
    accrualTarget: row?.accrualTarget || "",
    currentAccrual: row?.currentAccrual || ""
  };

  const handleSave = (values) => {
    if (values.status.value === 'OPEN' && selectedCohort?.status === 'CLOSED') {
      openConfirmation({
        message: 'The arm status cannot be changed as the cohort is Closed.',
        title: 'Status Change Not Allowed',
        hideButton1: true,
        btn2: 'Close'
      });
      return;
    }


    const confirmSaveArm = () => {
      let statusDateVal = values.statusDate;

      if (values.status.value !== row?.status && values.status.value !== 'CLOSED') {
        const newDate = new Date();
        statusDateVal = format(newDate, "dd-MMM-yyyy");
      }
      
      const apiStatusDateVal = getApiDTNZFormat(statusDateVal);

      const editedArmData = {
        ...values,
        status: values.status.value,
        statusDate: apiStatusDateVal
      };
      const armObj = { ...row, ...editedArmData, pmcId: selectedCohort.pmcId };

      if (saveHandler) {
        saveHandler(armObj, setEditing);
      } else {
        // eslint-disable-next-line no-console
        setEditing(false);
      }
    };

    if (values.status.value !== row?.status && values.status.value === 'CLOSED') {
      openConfirmation({
        message: `Please confirm you want to proceed. By updating the status to Closed, no additional patients will be randomized to ${row?.stratumName}: ${row?.description}.`,
        onAction: confirmSaveArm,
        title: 'Confirm Status Change',
        btn1: 'Submit',
        btn2: 'Cancel',
        onHover: true,
        hoverText1: 'Submit change to status',
        hoverText2: 'Cancel changes'
      });
    } else {
      openConfirmation({
        message: 'Do you want to save arm record?',
        onAction: confirmSaveArm,
        name: 'arm'
      });
    }
  };


  const validate = (values) => {
    const errors = {};
    let filteredArms = null;

    if (armData) {
      filteredArms = armData.filter(item => item.pmsId !== row?.pmsId);
    }
    // Validate arm name is not empty
    if (!values.stratumName || !values.stratumName?.trim()) {
      errors.stratumName = 'Arm is required.';
    }
    // Validate descriptio is not empty
    if (!values.description || !values.description?.trim()) {
      errors.description = 'Description is required.';
    }
    // Validate status is required
    if (!values.status || !values.status.value) {
      errors.status = 'Status is required.';
    }

    // Validate accrual target is required
    if (!values.accrualTarget || !values.accrualTarget.toString().trim()) {
      errors.accrualTarget = 'Accrual Target is required.';
    }

    // Validate arm name uniqueness if field is editable
    if (values.stratumName && editableFields.stratumName) {
      // validateStratumName(values.stratumName, filteredArms, errors);

      // check for the character count not exceeding 3 limit
      if (values.stratumName?.length > armTextLength) {
        errors.stratumName = 'Cannot exceed 3 characters.';
      }
      const armNames = filteredArms?.map(item => item.stratumName.toLowerCase());
      if (armNames.includes(values.stratumName.toLowerCase().trim())) {
        errors.stratumName = 'Arms must be unique.';
      }
    }

    // validate character count for description, reason
    if (values.description && editableFields.description) {

      // check for the character count not exceeding 250 limit
      if (values.description?.length > armDescriptionLength) {
        errors.description = 'Cannot exceed 250 characters.';
      }
    }

    if (values.statusReason && editableFields.statusReason) {
      // check for the character count not exceeding 500 limit
      if (values.statusReason?.length > armReasonLength) {
        errors.statusReason = 'Cannot exceed 500 characters.';
      }
    }
    // Add other validations as needed
    // prot- 745, as per this requirement, we don't need below check.
    // if (values.accrualTarget || values.currentAccrual) {
    //   const target = parseInt(values.accrualTarget, 10);
    //   const current = parseInt(values.currentAccrual, 10);
    //   if (current > target) {
    //     errors.accrualTarget = 'Current accrual cannot exceed target.';
    //   }
    // }
    return errors;
  };

  const onCancel = () => {
    setEditing(false);
  };

  // Helper component for table cell wrapper

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSave}
      validate={validate}
    >
      {props => (
        <tr>
          <FormFieldCell fieldName="stratumName" label="stratumName">
            <EditableField name="stratumName" showError editableFields={editableFields} length={armTextLength} />
          </FormFieldCell>

          <FormFieldCell fieldName="description" label="Description">
            <EditableField name="description" showError editableFields={editableFields} length={armDescriptionLength} />
          </FormFieldCell>

          <FormFieldCell fieldName="status" label="Status">
            <Select
              name="status"
              options={statusRecordOptions}
              menuPortalTarget={document.body}
              onChange={(selectedVal) => {
                props.setFieldValue("status", selectedVal);
              }}
              value={props.values.status}
              isDisabled={!editableFields?.status}
              className={`reactSelect-wrapper ${!editableFields?.status ? 'disabled-field' : ''}`}
              classNamePrefix="reactSelect"
              styles={{ control: (provided) => ({ ...provided, minWidth: '8rem' }) }}
            />
            {props.errors && props.errors.status && (
              <TextError>{props.errors.status}</TextError>
            )}
          </FormFieldCell>

          <FormFieldCell fieldName="statusDate" label="Status Date">
            <EditableField name="statusDate" editableFields={editableFields} />
          </FormFieldCell>

          <FormFieldCell fieldName="statusReason" label="Reason">
            <EditableField name="statusReason" showError editableFields={editableFields} length={armReasonLength} />
          </FormFieldCell>

          <FormFieldCell fieldName="accrualTarget" label="Accrual Target">
            <EditableField name="accrualTarget" showError type="number" editableFields={editableFields} />
          </FormFieldCell>

          <FormFieldCell fieldName="currentAccrual" label="Current Accrual">
            <EditableField name="currentAccrual" type="number" editableFields={editableFields} />
          </FormFieldCell>

          <td>
            <span className="action-icon-container whiteSpace-nowrap">
              <button
                type="submit"
                aria-label="Submit"
                className="grid-fn-btn"
                title="Submit"
                disabled={Object.keys(props.errors).length > 0}
                onClick={props.handleSubmit}
              >
                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="save" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="svg-inline--fa fa-save">
                  <path fill="currentColor" d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM224 416c-35.346 0-64-28.654-64-64 0-35.346 28.654-64 64-64s64 28.654 64 64c0 35.346-28.654 64-64 64zm96-304.52V212c0 6.627-5.373 12-12 12H76c-6.627 0-12-5.373-12-12V108c0-6.627 5.373-12 12-12h228.52c3.183 0 6.235 1.264 8.485 3.515l3.48 3.48A11.996 11.996 0 0 1 320 111.48z" />
                </svg>
              </button>
              <button
                onClick={onCancel}
                aria-label="Cancel"
                type="button"
                className="grid-fn-btn close"
                title="Cancel"
              >
                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="times" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 512" className="svg-inline--fa fa-times">
                  <path fill="currentColor" d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z" />
                </svg>
              </button>
              <button
                onClick={() => props.resetForm()}
                aria-label="Reset"
                type="button"
                className="grid-fn-btn"
                title="Reset"
              >
                <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="undo-alt" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="svg-inline--fa fa-undo-alt">
                  <path fill="currentColor" d="M255.545 8c-66.269.119-126.438 26.233-170.86 68.685L48.971 40.971C33.851 25.851 8 36.559 8 57.941V192c0 13.255 10.745 24 24 24h134.059c21.382 0 32.09-25.851 16.971-40.971l-41.75-41.75c30.864-28.899 70.801-44.907 113.23-45.273 92.398-.798 170.283 73.977 169.484 169.442C423.236 348.009 349.816 424 256 424c-41.127 0-79.997-14.678-110.63-41.556-4.743-4.161-11.906-3.908-16.368.553L89.34 422.659c-4.872 4.872-4.631 12.815.482 17.433C133.798 479.813 192.074 504 256 504c136.966 0 247.999-111.033 248-247.998C504.001 119.193 392.354 7.755 255.545 8z" />
                </svg>
              </button>
            </span>
          </td>
        </tr>
      )}
    </Formik>
  );
}
